import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Alert, Backdrop, Box, Button, CircularProgress, Typography } from '@mui/material';
import MenuCourseSection from '../components/MenuCourseSection';
import MenuFileUpload from '../components/MenuFileUpload';
import MenuPrefillImportDialog from '../components/MenuPrefillImportDialog';
import MenuSkipConfirmDialog from '../components/MenuSkipConfirmDialog';
import OrDivider from '../components/OrDivider';
import { getEmptyDish } from '../manageEventForm';
import { BRAND_ORANGE } from '../../../../styles/portalTheme';
import {
  applyExtractedMenuItems,
  countMenuDishesWithContent,
  isMenuEmpty,
} from '../menuPrefillUtils';
import { useExtractMenuItemsMutation } from '../../../../api/use-extract-menu-items-mutation';
import {
  isMenuPrefillFileAllowed,
  MENU_PREFILL_FILE_ACCEPT,
  MENU_PREFILL_FILE_FORMAT_HINT,
} from '../eventFormConstants';

const MenuStep = ({ form, errors = {}, onChange, onSkipMenu }) => {
  const extractMenuItemsMutation = useExtractMenuItemsMutation();
  const isPrefillLoading = extractMenuItemsMutation.isPending;
  const [prefillError, setPrefillError] = useState('');
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);
  const [pendingPrefillFile, setPendingPrefillFile] = useState(null);
  const [existingDishCount, setExistingDishCount] = useState(0);

  const updateSectionDishes = (sectionIndex, dishes) => {
    const menuSections = form.menuSections.map((section, i) =>
      i === sectionIndex ? { ...section, dishes } : section,
    );
    onChange('menuSkipped', false);
    onChange('menuSections', menuSections);
  };

  const handleAddDish = (sectionIndex) => {
    const section = form.menuSections[sectionIndex];
    updateSectionDishes(sectionIndex, [...section.dishes, getEmptyDish()]);
  };

  const handleRemoveDish = (sectionIndex, dishIndex) => {
    const section = form.menuSections[sectionIndex];
    updateSectionDishes(
      sectionIndex,
      section.dishes.filter((_, index) => index !== dishIndex),
    );
  };

  const handleMenuAttachFileChange = (file) => {
    onChange('menuAttachFile', file);
    if (file) {
      onChange('menuPrefillFile', null);
    }
  };

  const runMenuExtract = async (file, mode) => {
    if (!file) return;

    setPrefillError('');

    try {
      const result = await extractMenuItemsMutation.mutateAsync(file);
      const nextSections = applyExtractedMenuItems(form.menuSections, result.menuItems, mode);
      onChange('menuSkipped', false);
      onChange('menuSections', nextSections);
      onChange('menuPrefillFile', null);
      toast.success(result.message || 'Menu items extracted successfully');
    } catch (error) {
      setPrefillError(error?.message || 'Failed to extract menu items.');
    } finally {
      setPendingPrefillFile(null);
    }
  };

  const hasMenuContent = () => !isMenuEmpty(form.menuSections) || Boolean(form.menuAttachFile);

  const handleSkipRemoveMenu = () => {
    onChange('menuSkipped', true);
    onChange(
      'menuSections',
      form.menuSections.map((section) => ({
        ...section,
        dishes: [],
      })),
    );
    onChange('menuAttachFile', null);
    onChange('menuPrefillFile', null);
    setSkipDialogOpen(false);
    onSkipMenu?.();
  };

  const handleSkipKeepMenu = () => {
    onChange('menuSkipped', false);
    setSkipDialogOpen(false);
    onSkipMenu?.();
  };

  const handleSkipMenu = () => {
    if (hasMenuContent()) {
      setExistingDishCount(countMenuDishesWithContent(form.menuSections));
      setSkipDialogOpen(true);
      return;
    }

    handleSkipRemoveMenu();
  };

  const handleSkipDialogClose = () => {
    setSkipDialogOpen(false);
  };

  const handleImportConfirm = (mode) => {
    const file = pendingPrefillFile;
    if (!file || isPrefillLoading) return;

    setImportDialogOpen(false);
    setPendingPrefillFile(null);
    setExistingDishCount(0);
    void runMenuExtract(file, mode);
  };

  const handlePrefillFileSelected = (file) => {
    if (!file || isPrefillLoading) return;

    const dishCount = countMenuDishesWithContent(form.menuSections);

    if (dishCount === 0) {
      void runMenuExtract(file, 'replace');
      return;
    }

    setExistingDishCount(dishCount);
    setPendingPrefillFile(file);
    setImportDialogOpen(true);
  };

  const handleImportDialogClose = () => {
    if (isPrefillLoading) return;
    setImportDialogOpen(false);
    setPendingPrefillFile(null);
    setExistingDishCount(0);
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {errors.menu ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.menu}
        </Alert>
      ) : null}

      {prefillError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPrefillError('')}>
          {prefillError}
        </Alert>
      ) : null}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          type="button"
          variant="outlined"
          onClick={handleSkipMenu}
          sx={{
            borderColor: BRAND_ORANGE,
            color: BRAND_ORANGE,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { borderColor: BRAND_ORANGE, bgcolor: 'rgba(224, 104, 10, 0.06)' },
          }}
        >
          Skip Menu
        </Button>
      </Box>

      <Backdrop
        open={isPrefillLoading}
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: (theme) => theme.zIndex.modal + 1,
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          pt: 20,
          gap: 1.5,
          color: BRAND_ORANGE,
          borderRadius: 2,
          backgroundColor: (theme) =>
            theme.palette.mode === 'dark' ? 'rgba(18, 17, 16, 0.72)' : 'rgba(255, 255, 255, 0.82)',
        }}
      >
        <CircularProgress color="inherit" />
        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
          Extracting menu items…
        </Typography>
      </Backdrop>

      <Box sx={{ pointerEvents: isPrefillLoading ? 'none' : 'auto' }}>
        <MenuFileUpload
          label="Attach file for Menu Items"
          file={form.menuAttachFile}
          onFileChange={handleMenuAttachFileChange}
          disabled
        />

        <OrDivider />

        <MenuFileUpload
          label="Upload file to Pre-fill Menu Items"
          buttonOnly
          file={null}
          onFileChange={handlePrefillFileSelected}
          accept={MENU_PREFILL_FILE_ACCEPT}
          validateFile={isMenuPrefillFileAllowed}
          formatHint={MENU_PREFILL_FILE_FORMAT_HINT}
          invalidFileMessage="Please select a PDF or image file (PNG, JPG or JPEG)."
          disabled={isPrefillLoading}
        />

        <Box sx={{ mt: 3 }} />

        {form.menuSections.map((section, index) => (
          <MenuCourseSection
            key={section.course}
            section={section}
            sectionIndex={index}
            isLast={index === form.menuSections.length - 1}
            errors={errors}
            disabled={isPrefillLoading}
            onUpdateSection={updateSectionDishes}
            onAddDish={handleAddDish}
            onRemoveDish={handleRemoveDish}
          />
        ))}
      </Box>

      <MenuPrefillImportDialog
        open={importDialogOpen}
        fileName={pendingPrefillFile?.name}
        existingDishCount={existingDishCount}
        onClose={handleImportDialogClose}
        onAppend={() => handleImportConfirm('append')}
        onReplace={() => handleImportConfirm('replace')}
      />

      <MenuSkipConfirmDialog
        open={skipDialogOpen}
        dishCount={existingDishCount}
        onClose={handleSkipDialogClose}
        onRemoveAndSkip={handleSkipRemoveMenu}
        onKeepAndSkip={handleSkipKeepMenu}
      />
    </Box>
  );
};

export default MenuStep;
